import { makeAutoObservable } from "mobx";
import type { Map as MapboxMap } from "mapbox-gl";
import { mapStyles } from "@/config/map";

const BUILDINGS_LAYER_ID = "3d-buildings";
const TERRAIN_SOURCE_ID = "mapbox-dem";

/**
 * Owns the per-map background style + 3D buildings + terrain state and the
 * mapbox layer wiring behind them. Each <MapControls /> instance creates one.
 * Survives style changes — re-applies buildings/terrain on every style.load.
 */
export class MapControlsStore {
	styleId: string = mapStyles[0].id;
	buildings: boolean = false;
	terrain: boolean = false;

	private map: MapboxMap | null = null;

	constructor() {
		makeAutoObservable(this, {
			map: false,
		} as any);
	}

	attach = (map: MapboxMap) => {
		this.map = map;
		map.on("style.load", this.handleStyleLoad);
	};

	detach = () => {
		if (this.map) {
			this.map.off("style.load", this.handleStyleLoad);
		}
		this.map = null;
	};

	setStyle = (styleId: string) => {
		const style = mapStyles.find(candidate => candidate.id === styleId);
		if (!this.map || !style) return;
		this.styleId = styleId;
		this.map.setStyle(style.url);
		// handleStyleLoad re-applies buildings/terrain after the new style loads
	};

	toggleBuildings = (on: boolean) => {
		this.buildings = on;
		if (!this.map) return;
		if (on) this.addBuildingsLayer();
		else this.removeBuildingsLayer();
	};

	toggleTerrain = () => {
		this.terrain = !this.terrain;
		if (!this.map) return;
		if (this.terrain) this.enableTerrainSource();
		else this.map.setTerrain(null);
	};

	private handleStyleLoad = () => {
		if (this.buildings) this.addBuildingsLayer();
		if (this.terrain) this.enableTerrainSource();
	};

	private addBuildingsLayer() {
		if (!this.map || this.map.getLayer(BUILDINGS_LAYER_ID)) return;
		const layers = this.map.getStyle().layers;
		const labelLayerId = layers?.find(
			layer => layer.type === "symbol" && (layer.layout as Record<string, unknown>)?.["text-field"],
		)?.id;
		this.map.addLayer(
			{
				id: BUILDINGS_LAYER_ID,
				source: "composite",
				"source-layer": "building",
				filter: ["==", "extrude", "true"],
				type: "fill-extrusion",
				minzoom: 15,
				paint: {
					"fill-extrusion-color": "#aaa",
					"fill-extrusion-height": ["get", "height"],
					"fill-extrusion-base": ["get", "min_height"],
					"fill-extrusion-opacity": 0.6,
				},
			},
			labelLayerId,
		);
	}

	private removeBuildingsLayer() {
		if (this.map && this.map.getLayer(BUILDINGS_LAYER_ID)) {
			this.map.removeLayer(BUILDINGS_LAYER_ID);
		}
	}

	private enableTerrainSource() {
		if (!this.map) return;
		if (!this.map.getSource(TERRAIN_SOURCE_ID)) {
			this.map.addSource(TERRAIN_SOURCE_ID, {
				type: "raster-dem",
				url: "mapbox://mapbox.mapbox-terrain-dem-v1",
				tileSize: 512,
				maxzoom: 20,
			});
		}
		this.map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: 1.5 });
	}
}
